import {
  createEntityAdapter,
  createSlice,
  PayloadAction,
  Update,
} from '@reduxjs/toolkit'

import {
  IAttachmentDTO,
  IUpdateAttachmentProgressModel,
  IUpdateAttachmentStatusModel,
} from './typings'
import { StoreData } from '../reducers/typings'

import { getAttachments, addAttachments, deleteAttachment } from './api'
import { toast } from '@sales-center/components'
import { FileSizeToString } from '@user/utils/FileSizeToString'
import { MimeTypeToFriendlyName } from '@user/utils/MimeTypeToFriendlyName'

export const attachmentsAdapter = createEntityAdapter<IAttachmentDTO>({
  selectId: (attachment) => attachment.id,
  sortComparer: (a, b) =>
    a.displayName.localeCompare(b.displayName) ||
    (a.createdDate < b.createdDate
      ? -1
      : a.createdDate > b.createdDate
        ? 1
        : 0),
})

export const initialState = attachmentsAdapter.getInitialState()

export const attachmentsSlice = createSlice({
  name: 'attachments',
  initialState: initialState,
  reducers: {
    reset: () => initialState,
    setProgress: (
      state,
      { payload }: PayloadAction<IUpdateAttachmentProgressModel>,
    ) => {
      const update: Update<IAttachmentDTO> = {
        id: payload.id,
        changes: {
          ...payload,
        },
      }
      attachmentsAdapter.updateOne(state, update)
    },
    setStatus: (
      state,
      { payload }: PayloadAction<IUpdateAttachmentStatusModel>,
    ) => {
      const update: Update<IAttachmentDTO> = {
        id: payload.id,
        changes: {
          ...payload,
        },
      }
      attachmentsAdapter.updateOne(state, update)
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getAttachments.fulfilled, (state, { payload }) => {
      attachmentsAdapter.setAll(state, payload)
    })

    builder.addCase(addAttachments.fulfilled, (state, { payload }) => {
      const tempEntity: Update<IAttachmentDTO> = {
        id: payload.tempId,
        changes: {
          attachmentType: payload.attachmentType,
          createdDate: new Date(payload.createdDate),
          displayName: payload.displayName,
          fileSize: payload.fileSize,
          id: payload.id,
          status: 'idle',
        },
      }
      attachmentsAdapter.updateOne(state, tempEntity)
    })

    builder.addCase(addAttachments.rejected, (state, { payload }) => {
      if (payload) {
        attachmentsAdapter.removeOne(state, payload.id)
        toast.error(payload.Message, true)
      }
    })

    builder.addCase(addAttachments.pending, (state, { meta }) => {
      const tempEntity: IAttachmentDTO = {
        attachmentType: MimeTypeToFriendlyName(meta.arg.mimeType),
        createdDate: new Date(),
        displayName: meta.arg.displayName,
        fileSize: FileSizeToString(meta.arg.fileSize),
        id: meta.arg.id,
        progress: 0,
        status: 'uploading',
        isCompletedFormPdf: false,
      }
      attachmentsAdapter.upsertOne(state, tempEntity)
    })

    builder.addCase(deleteAttachment.pending, (state, { meta }) => {
      const tempEntity: Update<IAttachmentDTO> = {
        id: meta.arg,
        changes: {
          status: 'deleting',
        },
      }
      attachmentsAdapter.updateOne(state, tempEntity)
    })

    builder.addCase(deleteAttachment.fulfilled, (state, { payload }) => {
      attachmentsAdapter.removeOne(state, payload)
    })

    builder.addCase(deleteAttachment.rejected, (state, { payload }) => {
      if (payload) {
        const tempEntity: Update<IAttachmentDTO> = {
          id: payload.id,
          changes: {
            status: 'idle',
          },
        }
        attachmentsAdapter.updateOne(state, tempEntity)
        toast.error(payload.Message)
      }
    })
  },
})

export default attachmentsSlice.reducer

export const attachmentsSelectors = attachmentsAdapter.getSelectors<StoreData>(
  (state) => state.attachments,
)

export const {
  selectById: selectAttachmentById,
  selectAll: selectAllAttachments,
} = attachmentsSelectors

export const { reset, setProgress, setStatus } = attachmentsSlice.actions
